import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { i18n } from '@/i18n';
import { BrandProvider, useMarchio } from './BrandProvider';
import { hostVetrina } from './marca';
import { DeviceProvider } from './DeviceProvider';
import { configAuth, richiedeLoginPer } from './auth';
import { usePreferenze } from './preferenze';
import { useSimulatore } from './simulatore';
import { Vetrina } from '@/layout/Vetrina';
import { AppShell } from '@/layout/AppShell';
import { TecnicoShell } from '@/layout/TecnicoShell';
import { PannelloPage } from '@/features/pannello/PannelloPage';
import { ZonePage } from '@/features/zone/ZonePage';
import { ProgrammazionePage } from '@/features/programmazione/ProgrammazionePage';
import { SanitarioPage } from '@/features/sanitario/SanitarioPage';
import { VentilazionePage } from '@/features/ventilazione/VentilazionePage';
import { NotifichePage } from '@/features/notifiche/NotifichePage';
import { ImpostazioniPage } from '@/features/impostazioni/ImpostazioniPage';
import { SimulatorePage } from '@/features/simulatore/SimulatorePage';
import { AccessoPage } from '@/features/tecnico/AccessoPage';
import { ParcoPage } from '@/features/tecnico/ParcoPage';
import { CanaliPage } from '@/features/tecnico/CanaliPage';
import { ConfigurazioneIoPage } from '@/features/tecnico/ConfigurazioneIoPage';
import { ParametriPage } from '@/features/tecnico/ParametriPage';
import { GeneratoriPage } from '@/features/tecnico/GeneratoriPage';
import { FirmwarePage } from '@/features/tecnico/FirmwarePage';
import { RegistroPage } from '@/features/tecnico/RegistroPage';

// Le schermate con i grafici caricano Recharts solo quando servono (RNF-14: bundle iniziale < 300 KB gzip).
const StoricoPage = lazy(() => import('@/features/storico/StoricoPage').then((m) => ({ default: m.StoricoPage })));
const DiagnosticaPage = lazy(() => import('@/features/tecnico/DiagnosticaPage').then((m) => ({ default: m.DiagnosticaPage })));

function Preferenze() {
  const lingua = usePreferenze((s) => s.lingua);
  const tema = usePreferenze((s) => s.tema);
  useEffect(() => {
    void i18n.changeLanguage(lingua);
    document.documentElement.lang = lingua;
  }, [lingua]);
  useEffect(() => {
    document.documentElement.dataset.theme = tema;
  }, [tema]);
  return null;
}

/**
 * Dove si atterra aprendo la radice.
 *
 * Su un host vetrina, `/simulatore`: chi arriva li' non ha un impianto da
 * guardare, ha una domanda su cosa sappia fare l'app — e il configuratore e'
 * la risposta, oltre a essere l'unica pagina che invita a fare qualcosa invece
 * che a leggere. Ovunque altro si atterra sul pannello, che e' l'impianto di
 * chi ha fatto l'accesso.
 */
function rottaIniziale(): string {
  if (typeof window === 'undefined') return '/pannello';
  return hostVetrina(window.location.hostname) ? '/simulatore' : '/pannello';
}

function Dispositivo({ children }: { children: React.ReactNode }) {
  const { marchio } = useMarchio();
  const simulatore = useSimulatore((s) => (s.attivo ? JSON.stringify(s.configurazione) : 'reale'));
  // Cambiare marchio cambia parco impianti: il provider viene ricreato. Lo
  // stesso vale per una configurazione simulata diversa — senza la chiave, il
  // parco cambierebbe sotto un impianto già selezionato che non esiste più.
  return <DeviceProvider key={`${marchio.id}:${simulatore}`}>{children}</DeviceProvider>;
}

// The login gate is imported lazily so aws-amplify never enters the demo
// bundle: App loads it only on a configured, non-demo host (`richiedeLoginQui`).
const AuthBoundary = lazy(() => import('../auth/AuthBoundary'));

function AppRoutes() {
  return (
    <Dispositivo>
      <Routes>
            <Route element={<AppShell />}>
              <Route index element={<Navigate to={rottaIniziale()} replace />} />
              <Route path="/pannello" element={<PannelloPage />} />
              <Route path="/zone" element={<ZonePage />} />
              <Route path="/programmazione" element={<ProgrammazionePage />} />
              <Route path="/storico" element={<Suspense fallback={null}><StoricoPage /></Suspense>} />
              <Route path="/sanitario" element={<SanitarioPage />} />
              <Route path="/ventilazione" element={<VentilazionePage />} />
              <Route path="/notifiche" element={<NotifichePage />} />
              <Route path="/impostazioni" element={<ImpostazioniPage />} />
              <Route path="/simulatore" element={<SimulatorePage />} />
            </Route>
            <Route path="/tecnico" element={<AccessoPage />} />
            <Route element={<TecnicoShell />}>
              <Route path="/tecnico/parco" element={<ParcoPage />} />
              <Route path="/tecnico/canali" element={<CanaliPage />} />
              <Route path="/tecnico/io" element={<ConfigurazioneIoPage />} />
              <Route path="/tecnico/parametri" element={<ParametriPage />} />
              <Route path="/tecnico/generatori" element={<GeneratoriPage />} />
              <Route path="/tecnico/diagnostica" element={<Suspense fallback={null}><DiagnosticaPage /></Suspense>} />
              <Route path="/tecnico/firmware" element={<FirmwarePage />} />
              <Route path="/tecnico/registro" element={<RegistroPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/pannello" replace />} />
          </Routes>
    </Dispositivo>
  );
}

/**
 * The login gate. It sits INSIDE the router and subscribes to the simulator
 * mode on purpose: both of its inputs change while the app is running —
 * navigating to `/simulatore`, and leaving the simulator again — and a gate
 * computed once at startup would strand the app on the wrong side of itself.
 * Leaving the simulator used to be the visible symptom: the tree would swap to
 * the cloud client while the auth boundary stayed unmounted, so the fleet
 * request went out with no token and the plant list failed to load.
 */
function Cancello() {
  const { pathname, search } = useLocation();
  const simulatore = useSimulatore((s) => s.attivo);
  const richiede = richiedeLoginPer(
    configAuth(),
    { hostname: window.location.hostname, pathname, search },
    simulatore,
  );
  if (!richiede) return <AppRoutes />;
  return (
    <Suspense fallback={<div style={{ padding: '2rem', textAlign: 'center' }} aria-busy="true" />}>
      <AuthBoundary>
        <AppRoutes />
      </AuthBoundary>
    </Suspense>
  );
}

export function App() {
  // On a configured host — an installer's, or a developer's own machine — a
  // login is required; on a showcase host, an unconfigured build, or an
  // explicit `/simulatore`, the app is the gated simulator and the auth stack
  // is never even loaded. BrowserRouter sits ABOVE the gate so LoginPage
  // shares the app's router and the gate can read the route.
  return (
    <BrandProvider>
      <Preferenze />
      <BrowserRouter>
        {/* Su un host vetrina l'app finisce dentro un tablet, con la nostra
            insegna sopra; altrove Vetrina e' trasparente. Sopra al cancello
            perche' la cornice non deve sparire durante un caricamento. */}
        <Vetrina>
          <Cancello />
        </Vetrina>
      </BrowserRouter>
    </BrandProvider>
  );
}
